import { mapGetters, mapState, mapActions } from 'vuex';
import { cloneDeep, set, debounce } from 'lodash-es';
import EventBus from '../utils/event-bus';

/**
 * requires elementsMixins to be imported in the component
 */
export default {
  data() {
    return {
      virtualElements: [],
      virtualElementsReversed: [],
    };
  },
  computed: {
    ...mapState({
      filteredData(state, getters) {
        const parent = this.getElementParentGroupId(this.id);
        const fn = getters[`${[parent]}/getFilteredData`] || Function.prototype;
        return fn(this.filters);
      },
    }),

    ...mapGetters('graphicsTemplate', [
      'getElementParentGroupId',
    ]),

    paginationIndex() {
      return this.filters.findIndex((filter) => filter.type === 'pagination');
    },

    paginationLimit: {
      get() {
        return this.element.data?.filters[this.paginationIndex].limit;
      },
      set(v) {
        this.updateOneElementValue(`data.filters.${this.paginationIndex}.limit`, v);
      },
    },

    filters() {
      return this.element.data?.filters || [];
    },

    settings() {
      return this.element.settings || {};
    },

    rawEditableElements() {
      return this.childElements?.map((e) => this.getElementById(e));
    },
  },
  watch: {
    'element.styles': {
      deep: true,
      handler() {
        this.debouncedMakeVirtualElements();
      },
    },
    filters: {
      deep: true,
      handler() {
        this.debouncedMakeVirtualElements();
      },
    },
    settings: {
      deep: true,
      handler() {
        this.debouncedMakeVirtualElements();
      },
    },
    rawEditableElements: {
      deep: true,
      handler() {
        this.debouncedMakeVirtualElements();
      },
    },
  },

  created() {
    this.debouncedMakeVirtualElements = debounce(this.makeVirtualElements, 100);

    this.debouncedMakeVirtualElements();
  },

  methods: {
    ...mapActions('graphicsTemplate', [
      'addNewElement',
    ]),

    async makeVirtualElements() {
      if (!this.dataProvider) return;
      const { layout, gap } = this.settings;
      const { width, height } = this.element.styles;
      const originalVirtualArr = [];
      const reversedVirtualArr = [];

      // loop over filtered data (or amount of data at least)
      // copy the element at that index and up the iteration
      // start at 1 instead of 0 since these will start with the 2nd data set
      for (let i = 1, j = this.paginationLimit; i < j; i += 1) {
        const reversedIterEls = [];
        const iterEls = this.childElements.map((elId, idx) => {
          const el = this.getElementById(elId);
          const copy = cloneDeep(el);

          // use set in case element is dragged in and doesn't have 'data'
          set(copy, 'data.iteration', i);
          set(copy, 'data.dataListId', this.element.id);
          copy.locked = true;
          copy.editable = false;
          copy.id = `virtual-${idx}-${i}-${this.element.id}`;

          if (layout === 'row') {
            // Horizontal
            const repeatOffset = (width + gap) * i;
            copy.styles.left += (repeatOffset);
          } else {
            // Vertical (default; fallback)
            const repeatOffset = (height + gap) * i;
            copy.styles.top += (repeatOffset);
          }

          // TODO: upddate instead if exists?
          this.addVirtualElement(copy.type, { settings: copy });

          // side effect to simultaneously create the reversed virtual elements arrays
          reversedIterEls.unshift(copy.id);

          return copy.id;
        });
        originalVirtualArr.push(iterEls);
        reversedVirtualArr.push(reversedIterEls);
      }

      const [original, reversed] = await Promise.all([
        Promise.all(originalVirtualArr),
        Promise.all(reversedVirtualArr),
      ]);

      this.virtualElements = original;
      this.virtualElementsReversed = reversed;

      const hasTextChildren = this.childElements.some((elId) => {
        const child = this.getElementById(elId);
        return child?.type === 'text';
      });

      if (hasTextChildren) {
        this.$nextTick(() => {
          EventBus.emit('resize-text', {
            // scope the update to only this element so we don't retrigger EVERYTHING
            dataListId: this.element.id,
          });
        });
      }
    },

    async addVirtualElement(type, elOptions) {
      const { settings } = elOptions;
      return this.addNewElement({
        value: {
          type,
          settings,
        },
        options: {
          blacklist: true,
          skipMapping: true,
        },
      });
    },
  },
};
